<template>
  <div>
    <div id="dropdown" />
    <div class="relative">
      <!-- Mobile menu -->
      <div 
        :class="{'block': mobileMenuOpen, 'hidden': !mobileMenuOpen}"
        class="md:hidden fixed inset-0 z-10 bg-white"
      >
        <div class="pt-16 pb-6 px-4">
          <main-menu 
            :account="auth.user.account"
            class="flex flex-col space-y-4"
            :style="{'backgroundColor': '#fff'}"
            @menu-item-clicked="closeMobileMenu"
          />
        </div>
      </div>

      <div class="md:flex md:flex-col">
        <div class="md:flex md:flex-col md:h-screen">
          <div class="md:flex md:flex-shrink-0">
            <div class="flex items-center justify-between bg-[#AEF5BC] md:flex-shrink-0 md:justify-center md:w-56 p-4">
              <Link class="mt-1 mb-1" href="/">
                <logo :src="auth.user.account.logo" class="fill-white" width="120" height="50" />
              </Link>
              <!-- Mobile menu button -->
              <button 
                @click="mobileMenuOpen = !mobileMenuOpen"
                class="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500"
              >
                <span class="sr-only">Abrir menú</span>
                <svg 
                  class="h-10 w-10" 
                  :class="{'hidden': mobileMenuOpen, 'block': !mobileMenuOpen }"
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  class="h-10 w-10"
                  :class="{'block': mobileMenuOpen, 'hidden': !mobileMenuOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none" 
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="md:text-md flex items-center justify-between p-4 w-full text-sm bg-white border-b md:px-12 md:py-0">
              <div>
                <Link href="/version" class="badge bg-gray-200 p-1 font-bold rounded-lg text-gray-500">{{ $page.props.appVersion }}-consan</Link>
                <Link href="/taxes" class="mr-4 mt-1 hidden md:block">1$ = {{ parseFloat(change).toFixed(2) }} BsD</Link>
              </div>
              <div class="font-semibold text-xl"></div>
              <div class="flex items-center">
                <button
                  v-if="$page.props.open_cash_register"
                  type="button"
                  @click="showCashAuditModal = true"
                  class="mr-3 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer"
                  title="Arqueo y Cierre de Caja"
                >
                  <i class="fa fa-calculator"></i>
                  <span class="hidden sm:inline">Arqueo / Cierre ({{ $page.props.open_cash_register.name }})</span>
                  <span class="sm:hidden">Cerrar Caja</span>
                </button>

                <dropdown class="mt-1" placement="bottom-end">
                  <template #default>
                    <div class="group flex items-center cursor-pointer select-none">
                      <div class="mr-1 text-gray-700 group-hover:text-blue-600 focus:text-blue-600 whitespace-nowrap">
                        <span>{{ auth.user.first_name }}</span>
                        <span class="hidden md:inline">&nbsp;{{ auth.user.last_name }}</span>
                      </div>
                      <icon class="w-5 h-5 fill-gray-700 group-hover:fill-blue-600 focus:fill-blue-600" name="cheveron-down" />
                    </div>
                  </template>
                  <template #dropdown>
                    <div class="mt-2 py-2 text-sm bg-white rounded shadow-xl">
                      <button
                        v-if="$page.props.open_cash_register"
                        type="button"
                        @click="showCashAuditModal = true"
                        class="block px-6 py-2 w-full text-left font-semibold text-emerald-700 hover:text-white hover:bg-emerald-600 cursor-pointer"
                      >
                        <i class="fa fa-calculator mr-2"></i>Arqueo / Cierre de Caja
                      </button>
                      <Link v-if="auth.user.role === 'admin'" class="block px-6 py-2 hover:text-white hover:bg-blue-500" :href="`/users/${auth.user.id}/edit`">Mi perfil</Link>
                      <Link v-if="auth.user.role === 'admin'" class="block px-6 py-2 hover:text-white hover:bg-blue-500" href="/users">Usuarios</Link>
                      <Link v-if="auth.user.role === 'admin'" class="block px-6 py-2 hover:text-white hover:bg-blue-500" :href="`/accounts/${auth.user?.account?.id}`">Cuenta</Link>
                      <button type="button" @click="handleLogout" class="block px-6 py-2 w-full text-left hover:text-white hover:bg-blue-500 cursor-pointer">Cerrar sesión</button>
                    </div>
                  </template>
                </dropdown>
              </div>
            </div>
          </div>
          <div class="md:flex md:flex-grow md:overflow-hidden">
            <main-menu 
              :account="auth.user.account" 
              class="hidden md:block flex-shrink-0 overflow-y-auto rounded-b-lg" 
              :style="{'backgroundColor': auth.user.account.sidebarColor ?? 'white'}" 
            />
            <div class="px-4 py-8 md:flex-1 md:p-12 md:overflow-y-auto" scroll-region>
              <flash-messages />
              <slot />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Arqueo y Cierre Global -->
    <cash-audit-modal
      v-if="$page.props.open_cash_register"
      :show="showCashAuditModal"
      :cash-register-id="$page.props.open_cash_register.id"
      :register-name="$page.props.open_cash_register.name || ''"
      :cashier-name="auth.user.first_name"
      :is-admin="auth.user.role === 'admin'"
      :is-logging-out="isLoggingOut"
      @close="showCashAuditModal = false; isLoggingOut = false"
    />
  </div>
</template>

<style scoped>
.background {
  background-color: #bbe8e1;
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Logo from '@/Shared/Logo'
import Dropdown from '@/Shared/Dropdown'
import MainMenu from '@/Shared/MainMenu'
import FlashMessages from '@/Shared/FlashMessages'
import BottomMenu from './BottomMenu.vue'
import CashAuditModal from '@/Shared/CashAuditModal'

export default {
  components: {
    Dropdown,
    FlashMessages,
    Icon,
    Link,
    Logo,
    MainMenu,
    BottomMenu,
    CashAuditModal
  },
  props: {
    auth: Object,
    change: String,
    change_buy: Number,
    appVersion: String // Add this prop
  },
  data() {
    return {
      date: '',
      time: '',
      mobileMenuOpen: false,
      showCashAuditModal: false,
      isLoggingOut: false
    }
  },
  mounted() {
    // Mostrar mensajes flash como toasts
    if (this.$page.props.flash.success) {
      this.$toast.success(this.$page.props.flash.success);
    }
    if (this.$page.props.flash.error) {
      this.$toast.error(this.$page.props.flash.error);
    }
    if (this.$page.props.flash.warning) {
      this.$toast.warning(this.$page.props.flash.warning);
    }
    if (this.$page.props.flash.info) {
      this.$toast.info(this.$page.props.flash.info);
    }
  },
  watch: {
    '$page.url'() {
      this.closeMobileMenu()
    }
  },
  methods: {
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    zeroPadding: (num, digit) => {
      var zero = '';
      for(var i = 0; i < digit; i++) {
        zero += '0';
      }
      return (zero + num).slice(-digit);
    },
    update: () => {
      let cd = new Date();
      let hours = '';
      let minutes = '';
      let secounds = '';
      let zero = '';

      for(let i = 0; i < 1; i++) {
        zero += '0';
      }
      hours = cd.getHours() > 9 ? cd.getHours() : (zero + cd.getHours())
      minutes =  cd.getMinutes() > 9 ? cd.getMinutes() : (zero + cd.getMinutes())
      secounds = cd.getSeconds() > 9 ? cd.getSeconds() :(zero + cd.getSeconds())

      return hours + ':' + minutes + ':' + secounds
    },
    handleLogout() {
      const openBox = this.$page.props.open_cash_register
      if (openBox) {
        const boxName = openBox.name || 'tu caja'
        if (confirm(`No puedes cerrar sesión porque tienes una caja abierta (${boxName}). Debes realizar el arqueo y cierre de tu caja primero.\n\n¿Deseas abrir la ventana de arqueo y cierre de caja ahora?`)) {
          this.isLoggingOut = true
          this.showCashAuditModal = true
        }
        return
      }
      this.$inertia.delete('/logout')
    }
  }
}
</script>
